import { useEffect, useMemo, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';

import { useGetGroupedDevices } from '../../queries/devices';
import { toggleDialog } from '../../redux/Slices/Global/globalSlice';
import { useTitle } from '../../config/global';
import { useIsRestaurant } from '../../hooks/useAccountType';
import { resolveDeviceOnline } from '../../utils';
import "./css/device.css";
import "./css/responsiveDevice.css";
import "./css/screensHoney.css";
import "./css/screenMerged.css";
import { HealthProvider, useHealth } from './Component/Health/HealthContext';
import NeedsYouBanner from './Component/Health/NeedsYouBanner';
import { needsOwner } from './Component/Health/model';
import { filterGroupItems, filterGroupsByStatus } from './screenFilters';


import CreateGroup from './Component/Grouping/CreateGroup';
import Confirm from './Component/Confirm';
import Layout from '../Layout/Layout'
import Group from './Component/Grouping/Group'

import { setDeviceFilters } from '../../redux/Slices/deviceSlice';
import FirebaseMediaListener from '../Services/FirebaseMediaListener';
import ScreenshotPreviewer from './Component/Preview/ScreenshotPreviewer';
import GroupAssignCampaignsDropDown from './Component/CampaignAssignment/GroupAssignCampaignsDropDown';

// Icon set — exact paths from .claude/pp-screens-prototype.html, so the
// toolbar/state visuals match the approved (simplified-IA) prototype exactly.
const IcGroup = () => <svg viewBox="0 0 24 24"><rect x="3" y="4" width="7" height="7" rx="1.5" /><rect x="14" y="4" width="7" height="7" rx="1.5" /><rect x="3" y="15" width="7" height="7" rx="1.5" /><path d="M17.5 15v6M14.5 18h6" /></svg>;
const IcScreen = () => <svg viewBox="0 0 24 24"><rect x="2" y="4" width="20" height="13" rx="2" /><path d="M8 21h8M12 8.5v4M10 10.5h4" /></svg>;
const IcHealth = () => <svg viewBox="0 0 24 24"><path d="M3 12h4l2.5-6 4 12 2.5-6H21" /></svg>;
const IcRetry = () => <svg viewBox="0 0 24 24"><path d="M21 12a9 9 0 1 1-3-6.7M21 4v5h-5" /></svg>;
const IcError = () => <svg viewBox="0 0 24 24"><path d="M12 8v5M12 16.5v.5" /><circle cx="12" cy="12" r="9" /></svg>;

function IndexPage() {
    const { t } = useTranslation();
    useTitle(t('screen.plural'));
    const { data, isLoading, isError, refetch } = useGetGroupedDevices();
    const { companyDetails } = useSelector((state) => state.global);
    const deviceFiltersIsOnline = useSelector((state) => state.device.deviceFilters?.isOnline);
    const openDialog = useSelector((state) => state.global.openDialog);
    const disableBtn = useSelector((state) => state.global.disableBtn);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { customer: health } = useHealth();
    // "Need you" filter (client-side, on top of the existing server-side status filter): a screen that has a health fault,
    // is offline, or has its app closed — the same set the banner counts.
    const [needOnly, setNeedOnly] = useState(false);
    const needIds = useMemo(() => new Set((health?.screens || []).filter((s) => needsOwner(s.state)).map((s) => s.id)), [health]);


    const [groups, setGroups] = useState([]);

    useEffect(() => {
        if (!_.isEmpty(data)) {
            setGroups(data);
        }
    }, [data]);

    // One chip is active at a time (the highlight says which): «Need you» wins, else the status chip, else everything. All of them
    // only REMOVE screens from the list the backend sent, so the order (the customer's saved order) is never touched.
    const statusFilterActive = !needOnly && deviceFiltersIsOnline != null;
    const shownGroups = useMemo(() => {
        if (needOnly) return filterGroupItems(groups, (d) => needIds.has(d.id));
        return filterGroupsByStatus(groups, deviceFiltersIsOnline);
    }, [groups, needOnly, needIds, deviceFiltersIsOnline]);

    const isRestaurant = useIsRestaurant();

    // Real counts — every device across every group, status resolved the
    // SAME way each DeviceCard resolves its own (resolveDeviceOnline: 100%
    // backend connectivity.state, unified-presence rollout part 2,
    // 2026-10-06). No separate summary endpoint exists, so this is a
    // client-side reduce over the already-fetched grouped payload — not a
    // new data source.
    const counts = useMemo(() => {
        const allDevices = Object.values(groups || {}).flatMap((g) => g.items || []);
        const online = allDevices.filter((d) => resolveDeviceOnline(d.connectivity, d.online)).length;
        return { total: allDevices.length, online, offline: allDevices.length - online };
    }, [groups]);

    const handleToggleDialog = useCallback((dialogName) => () => {
        dispatch(toggleDialog(dialogName));
    }, [dispatch]);

    // Segmented status filter (fix d: surfaces the already-built but
    // previously unrendered Filter.jsx as a 3-way toggle). Same redux field
    // and same query-invalidation Filter.jsx already used — only the visual
    // form changed (a single-select segmented control instead of a
    // dropdown+submit form), to match the approved prototype's .seg exactly.
    // The backend list has no status filter (see screenFilters.js), so a chip is a pure client-side view: no refetch, no disabled buttons.
    const setStatusFilter = useCallback((isOnline) => {
        dispatch(setDeviceFilters({ isOnline }));
    }, [dispatch]);

    const openNewDevice = useCallback(() => dispatch(toggleDialog("confirmDialog")), [dispatch]);

    const renderDialogs = useMemo(() => (
        <>
            {openDialog.confirmDialog && <Confirm />}
            {openDialog.createGroupDialog && <CreateGroup />}
            {openDialog.GroupAssignCampaignsDropDown && <GroupAssignCampaignsDropDown />}
            {openDialog.screenshot && <ScreenshotPreviewer />}

        </>
    ), [openDialog]);

    const hasAnyGroups = !_.isEmpty(groups);
    // Only replace the whole page with the error/retry state when there's
    // truly nothing to show yet (first load failed) — a transient poll
    // error with existing data on screen keeps the pre-existing toast-only
    // behavior (useGetGroupedDevices' own onError) instead of flashing the
    // page to an error state every 15s if one poll blips.
    const showFullPageError = isError && !hasAnyGroups;

    const renderContent = () => {
        if (showFullPageError) {
            return (
                <div className="pp-scr-statecard">
                    <div className="pp-scr-empty err">
                        <div className="eic"><IcError /></div>
                        <div className="et">{t('devices.error_title')}</div>
                        <div className="es">{t('devices.error_desc')}</div>
                        <button type="button" className="pp-scr-btn ghost" style={{ marginTop: 4 }} onClick={() => refetch()}>
                            <IcRetry />{t('devices.retry')}
                        </button>
                    </div>
                </div>
            );
        }

        if (isLoading) {
            return (
                <div className="pp-scr-grid">
                    {Array.from({ length: 6 }).map((_unused, i) => (
                        <div className="pp-scr-skcard" key={i}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                                <div className="pp-scr-sk dot" />
                                <div className="pp-scr-sk a" />
                            </div>
                            <div className="pp-scr-sk badge" />
                            <div className="pp-scr-sk b" />
                            <div className="pp-scr-sk b" style={{ width: '60%' }} />
                        </div>
                    ))}
                </div>
            );
        }

        if (!hasAnyGroups) {
            return (
                <div className="pp-scr-statecard">
                    <div className="pp-scr-empty">
                        <div className="eic"><IcScreen /></div>
                        <div className="et">{t('devices.empty_title')}</div>
                        <div className="es">{t('devices.empty_desc')}</div>
                        <button type="button" className="pp-scr-btn primary" style={{ marginTop: 4 }} onClick={openNewDevice} disabled={disableBtn}>
                            ＋ {t('devices.new_device')}
                        </button>
                    </div>
                </div>
            );
        }

        if (needOnly && _.isEmpty(shownGroups)) {
            return (
                <div className="pp-scr-statecard">
                    <div className="pp-scr-empty">
                        <div className="et">{t('sh.none_need_title')}</div>
                        <div className="es">{t('sh.none_need_body')}</div>
                    </div>
                </div>
            );
        }
        if (statusFilterActive && _.isEmpty(shownGroups)) {
            return (
                <div className="pp-scr-statecard" data-testid="status-filter-empty">
                    <div className="pp-scr-empty">
                        <div className="et">{t('devices.filter_none_title')}</div>
                        <div className="es">{t('devices.filter_none_body')}</div>
                    </div>
                </div>
            );
        }
        // While a filter hides some screens, dragging a screen would save the order of only the visible ones -- locked until the filter is cleared.
        return <Group devicesGroup={shownGroups} reorderLocked={needOnly || statusFilterActive} />;
    };

    return (
        <Layout subNavbarProps={{ title: '' }}>
            <div className="pp-scr-head">
                <div>
                    <h1>{isRestaurant ? t('nav.vw_my_devices') : t('nav.screens')}</h1>
                    {companyDetails?.company?.name && (
                        <div className="pp-scr-sub">{companyDetails.company.name}</div>
                    )}
                </div>
            </div>

            {hasAnyGroups && !isLoading && (
                <div className="pp-scr-counts tnum">
                    <span><b>{counts.total}</b> {t('devices.screen_unit', { count: counts.total })}</span>
                    <span className="d">·</span>
                    <span style={{ color: 'var(--pp-btn-jade)' }}><b>{counts.online}</b> {t('devices.count_online_label')}</span>
                    <span className="d">·</span>
                    <span style={{ color: 'var(--pp-btn-coral)' }}><b>{counts.offline}</b> {t('devices.count_offline_label')}</span>
                </div>
            )}

            <NeedsYouBanner screens={health?.screens} onOpen={(id) => navigate(`/devices/${id}/show`)} />

            <div className="pp-scr-toolbar">
                <div className="pp-scr-seg">
                    <button type="button" className={!needOnly && deviceFiltersIsOnline == null ? 'on' : ''} onClick={() => { setNeedOnly(false); setStatusFilter(null); }} disabled={disableBtn}>{t('devices.filter_all')}</button>
                    <button type="button" className={!needOnly && deviceFiltersIsOnline === 1 ? 'on' : ''} onClick={() => { setNeedOnly(false); setStatusFilter(1); }} disabled={disableBtn}>{t('devices.filter_online')}</button>
                    <button type="button" className={!needOnly && deviceFiltersIsOnline === 0 ? 'on' : ''} onClick={() => { setNeedOnly(false); setStatusFilter(0); }} disabled={disableBtn}>{t('devices.filter_offline')}</button>
                    <button type="button" className={needOnly ? 'on' : ''} onClick={() => setNeedOnly(true)} data-testid="need-you-filter">{t('sh.filter_need')}{needIds.size ? <> · <span className="tnum">{needIds.size}</span></> : null}</button>
                </div>
                <div className="pp-scr-tspace" />

                {/* Simplified IA: only 3 toolbar controls now (filter
                    segment above + these two) — device order, group
                    reorder, and the page-level schedule shortcut all
                    retired/relocated (see Group.jsx/GroupHeader.jsx: group
                    reorder lives in the drag handle + the group ⋮ menu's
                    move up/down; device order folded into that same ⋮
                    menu; the schedule shortcut is redundant with the
                    sidebar's own "الجدول" page). No responsive collapse
                    needed anymore — 3 controls fit at every width. */}
                {/* Customer Screen health — fleet page (calm, plain-language status of every screen). */}
                <button type="button" className="pp-scr-btn ghost" onClick={() => navigate('/devices/health')} data-testid="screens-health-entry">
                    <IcHealth />{t('sh.nav_entry')}
                </button>
                <button type="button" className="pp-scr-btn ghost" onClick={handleToggleDialog("createGroupDialog")} disabled={disableBtn}>
                    <IcGroup />{t('devices.new_group')}
                </button>
                <button type="button" className="pp-scr-btn primary" onClick={openNewDevice} disabled={disableBtn}>
                    <IcScreen />{t('devices.new_device')}
                </button>
            </div>

            <div className='w-100'>
                {renderContent()}
            </div>
            {renderDialogs}

            <FirebaseMediaListener
                path={"/Companies/" + localStorage.getItem("company_id") + "/actions"}
                getData={refetch} />
        </Layout>
    );
}

export default function Index() {
    return (
        <HealthProvider>
            <IndexPage />
        </HealthProvider>
    );
}
